import {
    Box,
    Heading,
    Text,
    Card,
    CardBody,
    Flex,
    Badge,
    VStack,
    HStack,
    Spinner,
    Center,
    Button,
    IconButton,
    useToast,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Portal,
    useColorModeValue,
} from '@chakra-ui/react';
import { MdMoreVert, MdUndo, MdRefresh } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { usePursuits, useUpdatePursuit, useReturnToImported } from '@/services/hooks';

const STAGES = [
    { id: 'reviewing', label: 'Reviewing & Planning', color: 'blue' },
    { id: 'drafting', label: 'Drafting & Assembling', color: 'brand' },
    { id: 'submitted', label: 'Submitted', color: 'green' },
    { id: 'won', label: 'Won', color: 'teal' },
    { id: 'lost', label: 'Lost', color: 'red' },
];

function PursuitCard({ pursuit, index }: { pursuit: any; index: number }) {
    const navigate = useNavigate();
    const updateMutation = useUpdatePursuit();
    const returnMutation = useReturnToImported();
    const toast = useToast();

    const cardBg = useColorModeValue('white', 'navy.800');
    const titleColor = useColorModeValue('gray.800', 'white');
    const subColor = useColorModeValue('gray.600', 'gray.400');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

    const handleStageChange = async (newStage: string) => {
        try {
            await updateMutation.mutateAsync({ id: pursuit.id, data: { stage: newStage } });
            toast({ title: `Moved to ${newStage}`, status: 'success', duration: 2000 });
        } catch (e) {
            toast({ title: 'Failed to update stage', status: 'error' });
        }
    };

    const handleReturn = async () => {
        try {
            await returnMutation.mutateAsync(pursuit.id);
            toast({ title: 'Returned to Imported Bids', status: 'info' });
        } catch (e) {
            toast({ title: 'Failed to return', status: 'error' });
        }
    };

    const item = pursuit.imported_item || {};

    return (
        <Draggable draggableId={String(pursuit.id)} index={index}>
            {(provided, snapshot) => (
                <Box
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    mb={3}
                    opacity={snapshot.isDragging ? 0.8 : 1}
                >
                    <Card
                        size="sm"
                        variant="outline"
                        bg={cardBg}
                        borderColor={borderColor}
                        cursor="grab"
                        _hover={{ borderColor: 'brand.400', transform: 'translateY(-2px)', shadow: 'md' }}
                        transition="all 0.2s"
                        onClick={() => navigate(`/bid-room/${pursuit.id}`)}
                        boxShadow={snapshot.isDragging ? 'lg' : 'sm'}
                    >
                        <CardBody p={3}>
                            <Flex justify="space-between" align="flex-start" mb={2}>
                                <Badge colorScheme={item.opportunity_type === 'active' ? 'green' : 'gray'} fontSize="xs">
                                    {item.opportunity_type?.toUpperCase()}
                                </Badge>
                                <Menu isLazy>
                                    <MenuButton
                                        as={IconButton}
                                        icon={<MdMoreVert />}
                                        size="xs"
                                        variant="ghost"
                                        onClick={e => e.stopPropagation()}
                                    />
                                    <Portal>
                                        <MenuList zIndex={1400}>
                                            <MenuItem icon={<MdUndo />} onClick={handleReturn}>Return to Imported</MenuItem>
                                            {STAGES.filter(s => s.id !== pursuit.stage).map(stage => (
                                                <MenuItem key={stage.id} onClick={() => handleStageChange(stage.id)}>
                                                    Move to {stage.label}
                                                </MenuItem>
                                            ))}
                                        </MenuList>
                                    </Portal>
                                </Menu>
                            </Flex>

                            {item.item_code && (
                                <Text fontSize="xs" fontFamily="mono" color={subColor} mb={1}>{item.item_code}</Text>
                            )}
                            <Text fontWeight="bold" fontSize="sm" color={titleColor} noOfLines={2} mb={2}>
                                {item.description || item.project_title || 'Untitled Bid'}
                            </Text>

                            <Flex justify="space-between" align="center" mt={1}>
                                <Text fontSize="xs" color={subColor} isTruncated maxW="140px">
                                    {item.project_title || item.agency}</Text>
                                {pursuit.due_date && (
                                    <Text fontSize="xs" color="red.300" fontWeight="bold">
                                        {new Date(pursuit.due_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                    </Text>
                                )}
                            </Flex>
                        </CardBody>
                    </Card>
                </Box>
            )}
        </Draggable>
    );
}

function KanbanColumn({ stage, pursuits }: { stage: any; pursuits: any[] }) {
    // Sort pursuits to ensure consistent order (optional, but good for ID checks)
    const stagePursuits = pursuits.filter(p => p.stage === stage.id);

    const columnBg = useColorModeValue('gray.50', 'navy.900');
    const labelColor = useColorModeValue('gray.600', 'gray.300');
    const emptyBorder = useColorModeValue('gray.200', 'whiteAlpha.200');
    const draggingOverBg = useColorModeValue('gray.100', 'whiteAlpha.100');

    return (
        <Flex direction="column" bg={columnBg} p={3} borderRadius="lg" h="full">
            <Flex justify="space-between" align="center" mb={4} px={1}>
                <HStack>
                    <Box boxSize={2} borderRadius="full" bg={`${stage.color}.400`} />
                    <Text fontWeight="bold" fontSize="xs" textTransform="uppercase" color={labelColor}>
                        {stage.label}
                    </Text>
                </HStack>
                <Badge variant="subtle" colorScheme={stage.color} borderRadius="full">
                    {stagePursuits.length}
                </Badge>
            </Flex>

            <Droppable droppableId={stage.id}>
                {(provided, snapshot) => (
                    <VStack
                        spacing={0} // Spacing handled by card margin
                        align="stretch"
                        overflowY="auto"
                        flex={1}
                        pb={4}
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        bg={snapshot.isDraggingOver ? draggingOverBg : 'transparent'}
                        borderRadius="md"
                        transition="background 0.2s"
                        minH="150px"
                    >
                        {stagePursuits.map((p, index) => (
                            <PursuitCard key={p.id} pursuit={p} index={index} />
                        ))}
                        {provided.placeholder}

                        {stagePursuits.length === 0 && !snapshot.isDraggingOver && (
                            <Center py={10} border="1px dashed" borderColor={emptyBorder} borderRadius="md" mt={2}>
                                <Text fontSize="xs" color="gray.500">No items</Text>
                            </Center>
                        )}
                    </VStack>
                )}
            </Droppable>
        </Flex>
    );
}

export function Pipeline() {
    const { data: pursuits, isLoading, refetch } = usePursuits();
    const updateMutation = useUpdatePursuit();
    const toast = useToast();

    // Optimistic UI state could be managed here, but strictly relying on React Query cache 
    // invalidation is safer for consistency unless we implement full optimistic updates.
    // For now, we will just trigger the mutation.

    const onDragEnd = async (result: DropResult) => {
        const { destination, source, draggableId } = result;

        // Dropped outside or same position
        if (!destination) return;
        if (
            destination.droppableId === source.droppableId &&
            destination.index === source.index
        ) {
            return;
        }

        // If stage changed
        if (destination.droppableId !== source.droppableId) {
            const newStage = destination.droppableId;
            const pursuitId = parseInt(draggableId, 10);

            // Trigger mutation
            try {
                // We rely on React Query optimistic updates in useUpdatePursuit or automatic refetch
                await updateMutation.mutateAsync({
                    id: pursuitId,
                    data: { stage: newStage }
                });

                toast({
                    title: `Moved to ${STAGES.find(s => s.id === newStage)?.label}`,
                    status: 'success',
                    duration: 1500,
                    isClosable: true,
                    position: 'bottom-right'
                });
            } catch (error) {
                toast({ title: "Failed to move item", status: 'error' });
            }
        }
    };

    if (isLoading) {
        return (
            <Center h="50vh">
                <Spinner size="xl" color="brand.500" />
            </Center>
        );
    }

    return (
        <Box h={{ base: 'auto', md: "calc(100vh - 185px)" }}>
            <Flex
                justify="space-between"
                align="center"
                mb={{ base: 4, md: 6 }}
                px={{ base: 2, md: 0 }}
            >
                <Heading size={{ base: 'md', md: 'lg' }}>My Pursuits</Heading>
                <Button leftIcon={<MdRefresh />} size="sm" onClick={() => refetch()}>Refresh</Button>
            </Flex>

            <DragDropContext onDragEnd={onDragEnd}>
                <Flex
                    gap={{ base: 3, md: 4 }}
                    h="full"
                    overflowX="auto"
                    pb={4}
                    px={{ base: 2, md: 0 }}
                    sx={{
                        '&::-webkit-scrollbar': { height: '8px' },
                        '&::-webkit-scrollbar-thumb': { bg: 'whiteAlpha.200', borderRadius: '4px' },
                        scrollSnapType: { base: 'x mandatory', md: 'none' }
                    }}
                >
                    {STAGES.map(stage => (
                        <Box key={stage.id} minW={{ base: '85vw', sm: '300px', md: '280px' }} scrollSnapAlign="center">
                            <KanbanColumn stage={stage} pursuits={pursuits || []} />
                        </Box>
                    ))}
                </Flex>
            </DragDropContext>
        </Box>
    );
}
